import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="pt-20 w-full px-4 md:px-6 lg:px-8 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的分类页面不存在</p>
          <Link to="/" className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 17c.com`,
    description: category.description,
    url: `https://17c.com/category/${category.slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 17c.com`}
        description={category.description}
        keywords={`17c.com,${category.name},17c.com${category.name}`}
        canonicalUrl={`https://17c.com/category/${category.slug}`}
        schema={categorySchema}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[{ label: '分类浏览' }, { label: category.name }]}
        />

        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className={`${category.icon} text-xl text-accent-700`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-foreground-500">{category.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.length > 0 ? (
                categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors duration-200 overflow-hidden"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors truncate-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-3 truncate-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span>{article.readTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <i className="ri-inbox-line text-4xl text-foreground-300 block mb-3"></i>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}